import { routing } from "@/i18n/routing";

/**
 * Marketing pages load the browser Sentry SDK after the page has loaded, to
 * keep it out of their first paint; every other page loads it immediately.
 */
const MARKETING_PATHNAMES = [
  "/",
  "/pricing",
  "/guides",
  "/guides/[slug]",
  "/scenarios",
  "/scenarios/[slug]",
  "/comparisons",
  "/comparisons/[slug]",
  "/articles",
  "/articles/[slug]",
  "/youtube-summarizer",
  "/youtube-summary-chatgpt",
  "/youtube-transcript",
  "/how-it-works",
  "/about",
  "/contact",
  "/connectors",
  "/affiliate",
  "/affiliate/terms",
  "/privacy",
  "/terms",
  "/cookies",
] as const satisfies readonly (keyof typeof routing.pathnames)[];

type LocalizedPathname = string | Partial<Record<string, string>>;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|\\]/g, "\\$&");
}

function marketingPathPattern(): RegExp {
  const pathnames: Record<string, LocalizedPathname> = routing.pathnames;
  const alternatives = routing.locales.flatMap((locale) =>
    MARKETING_PATHNAMES.map((pathname) => {
      const localized = pathnames[pathname];
      const template =
        typeof localized === "string"
          ? localized
          : (localized[locale] ?? pathname);
      const suffix = template === "/" ? "" : template;
      // Dynamic segments such as [slug] match one path segment.
      return escapeRegExp(`/${locale}${suffix}`).replace(
        /\[[^\]]+\]/g,
        "[^/]+",
      );
    }),
  );
  return new RegExp(`^(?:${alternatives.join("|")})/?$`);
}

const MARKETING_PATH = marketingPathPattern();

export function isMarketingPath(pathname: string): boolean {
  return MARKETING_PATH.test(pathname);
}
